/* eslint-disable indent */
import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DeleteIcon, QuestionOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { getPagesVisted } from '../../../../redux/providerBuilder/networkRequestStore/selectors';
import { updateProviderDetails } from '../../../../redux/providerBuilder/providerDetails';
import { getProviderDetails } from '../../../../redux/providerBuilder/providerDetails/selectors';
import { ProviderDetails } from '../../../../redux/providerBuilder/providerDetails/types';
import {
  deleteSelectedInstance,
  resetParamsAdded,
  setParamsAdded,
  updateResponseSelection,
} from '../../../../redux/providerBuilder/selectedInstances';
import {
  getParamsAdded,
  getSelectedInstances,
} from '../../../../redux/providerBuilder/selectedInstances/selectors';
import { getSelectedNetworkRequest } from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';
import { updateActiveStep, updateBackAction } from '../../../../redux/providerBuilder/stepperDetails';
import { getActiveStep } from '../../../../redux/providerBuilder/stepperDetails/selectors';
import {
  compareProviders,
  getUniqueItems,
} from '../../../../utils/provider-builder/instance-selection-utils';
import {
  editProvider,
  getProviderById,
  registerProvider,
  saveProviderNetworkRequest,
} from '../../../../utils/provider-builder/register-provider-utils';
import {
  generateProviderDescriptionFromGPT,
  generateProviderNameFromGPT,
} from '../../../../utils/provider-builder/request-config-utils';
import { getFirebaseIdToken } from '../../../../utils/utils';
import { getSelectedNetworkIndex } from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';

interface ProviderNameProps {
  setActiveStepInnerForm: (value: SetStateAction<number>) => void;
}

export const ProviderName = (props: ProviderNameProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get('isEdit');

  const providerDetails = useAppSelector(getProviderDetails);
  const selectedNetworkRequest = useAppSelector(getSelectedNetworkRequest);
  const getAllRequest = useAppSelector(getSelectedNetworkRequest);
  const selectedInstances = useAppSelector(getSelectedInstances);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);
  const pagesVisted = useAppSelector(getPagesVisted);
  const activeStep = useAppSelector(getActiveStep);
  const userType = useAppSelector((state) => state.user.userType);
  const paramsAdded = useAppSelector(getParamsAdded);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const countryCodes = require('../../../../utils/data/country-codes.json');

  const uniquePagesVisted = getUniqueItems([
    ...pagesVisted,
    ...(selectedNetworkRequest[selectedNetworkIndex]?.visitedUrls ?? []),
  ]);

  const [providerName, setProviderName] = useState(
    providerDetails.name ? providerDetails.name : ''
  );
  const [providerDescription, setProviderDescription] = useState(
    providerDetails.description ? providerDetails.description : ''
  );
  const [expectedPageUrl, setExpectedPageUrl] = useState(
    providerDetails.expectedPageUrl ? providerDetails.expectedPageUrl : ''
  );
  const [pageTitle, setPageTitle] = useState(
    providerDetails.pageTitle ? providerDetails.pageTitle : ''
  );
  const [stepsToFollow, setStepsToFollow] = useState(
    providerDetails.stepsToFollow ? providerDetails.stepsToFollow : ''
  );
  const [loginUrl, setLoginUrl] = useState(
    providerDetails.loginUrl ? providerDetails.loginUrl : ''
  );
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [loginUrlError, setLoginUrlError] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [providerNameLoading, setProviderNameLoading] = useState(false);
  const [providerDetailsLoading, setProviderDetailsLoading] = useState(false);
  const [showAdvanceFields, setShowAdvanceFields] = useState<boolean>(false);
  const [customInjection, setCustomInjection] = useState(
    providerDetails?.customInjection ?? ''
  );
  //do not delete
  // const [geoLocation, setGeoLocation] = useState(
  //   providerDetails?.geoLocation === '' ? '{{DYNAMIC_GEO}}' : (providerDetails?.geoLocation ?? '{{DYNAMIC_GEO}}')
  // );
  const [geoLocation, setGeoLocation] = useState(providerDetails?.geoLocation ?? '');
  const [providerType, setProviderType] = useState(
    providerDetails?.providerType ?? 'PRIVATE'
  );
  const [userAgent, setUserAgent] = useState(
    providerDetails?.userAgent ?? { ios: '', android: '' }
  );
  //do not delete
  // const [isDynamicGeo, setIsDynamicGeo] = useState<boolean>(
  //   providerDetails?.geoLocation === '{{DYNAMIC_GEO}}' || providerDetails?.geoLocation === ''
  // );

  useEffect(() => {
    const setDefaultValues = async () => {
      setProviderNameLoading(true);
      setProviderDetailsLoading(true);
      const providerName = await generateProviderNameFromGPT(
        selectedNetworkRequest[selectedNetworkIndex].url,
        selectedInstances
      );
      setProviderName(providerName);
      setProviderNameLoading(false);
      const providerDescription = await generateProviderDescriptionFromGPT(
        providerName,
        selectedNetworkRequest[selectedNetworkIndex]?.url,
        selectedInstances
      );
      setProviderDescription(providerDescription);
      setProviderDetailsLoading(false);
    };

    if (!providerName && !providerDescription) {
      setDefaultValues();
    }
  }, []);

  useEffect(() => {
    handleSubmit(providerDetails?.httpProviderId?.length > 0);
  }, [providerDetails]);

  const filteredAndSortedSuggestions = [...uniquePagesVisted]
    .filter((url) => url.toLowerCase().includes(loginUrl.toLowerCase()))
    .sort((a, b) => {
      // Sort exact matches first, then by how early the match appears
      const aIndex = a.toLowerCase().indexOf(loginUrl.toLowerCase());
      const bIndex = b.toLowerCase().indexOf(loginUrl.toLowerCase());
      if (aIndex === 0 && bIndex !== 0) {
        return -1;
      }

      if (bIndex === 0 && aIndex !== 0) {
        return 1;
      }

      return aIndex - bIndex;
    });

  const updateField = useCallback(
    (field: string, value: string) => {
      switch (field) {
        case 'name':
          setProviderName(value);
          break;
        case 'description':
          setProviderDescription(value);
          break;
        case 'expectedPageUrl':
          setExpectedPageUrl(value);
          break;
        case 'pageTitle':
          setPageTitle(value);
          break;
        case 'stepsToFollow':
          setStepsToFollow(value);
          break;
        case 'loginUrl':
          setLoginUrl(value);
          break;
        case 'geoLocation':
          setGeoLocation(value);
          break;
        case 'providerType':
          setProviderType(value);
          break;
      }

      dispatch(updateProviderDetails({ [field]: value }));
    },
    [dispatch]
  );

  async function handleSubmit(isEdit: boolean = false) {
    if (buttonLoading) {
      if (isEdit) {
        await editHttpProvider();
      } else {
        await submitHttpProvider();
      }

      setButtonLoading(false);
    }
  }

  function handleGoBack() {
    props.setActiveStepInnerForm(0);
  }

  async function handleNext() {
    let isError = false;
    if (!providerName?.trim()) {
      setNameError('Provider name is required');
      isError = true;
    }

    if (!providerDescription?.trim()) {
      setDescriptionError('Provider description is required');
      isError = true;
    }

    if (!loginUrl?.trim()) {
      setLoginUrlError('Login url is required');
      isError = true;
    }

    if (isError) {
      return toast({
        title: 'Error',
        description: 'Please fill all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }

    setButtonLoading(true);

    try {
      const allResponseSelections = selectedInstances?.map((instance: any) => {
        return {
          ...instance?.responseSelection,
          variableName: instance?.variableName,
          url:
            selectedNetworkRequest[instance?.responseSelection?.requestIndex]?.url ?? '',
          requestIndex: instance?.responseSelection?.requestIndex ?? '',
          method:
            selectedNetworkRequest[instance?.responseSelection?.requestIndex]?.method ??
            '',
        };
      });
      dispatch(
        updateProviderDetails({
          name: providerName,
          description: providerDescription,
          method: selectedNetworkRequest[selectedNetworkIndex].method,
          loginUrl: loginUrl,
          expectedPageUrl,
          pageTitle,
          stepsToFollow,
          providerType,
          customInjection,
          geoLocation,
          isApproved: !customInjection || userType === 'ADMIN',
          responseSelections: allResponseSelections,
          userAgent,
        })
      );
    } catch (error) {
      console.log('error', error);
    }
  }

  async function submitHttpProvider() {
    try {
      const accessToken = (await getFirebaseIdToken()) ?? '';
      const message = await registerProvider(
        providerDetails,
        selectedNetworkRequest,
        accessToken
      );
      if (!message.isSuccess) {
        toast({
          title: 'Provider Creation Failed',
          description: message.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      } else {
        dispatch(
          updateProviderDetails({
            httpProviderId: message.provider.httpProviderId,
            isActive: false,
          })
        );
        dispatch(updateActiveStep(activeStep + 1));
        setButtonLoading(false);

        let networkPayloads: {
          networkRequests: any[];
          httpProviderId: string;
          selectedInstances: any[];
          visitedUrls: string[];
          isCascading: boolean;
        } = {
          networkRequests: [],
          httpProviderId: message.provider.httpProviderId,
          selectedInstances: selectedInstances,
          visitedUrls: [...uniquePagesVisted],
          isCascading: true,
        };

        networkPayloads.networkRequests = selectedNetworkRequest.map((request) => {
          let payload = JSON.parse(JSON.stringify(request));

          delete payload['cookies'];
          delete payload['request']['headers'];

          return {
            ...payload,
          };
        });

        // (contains multiple network requests - if it is cascading provider)
        // TODO: check manual verification flow implementation and update this
        saveProviderNetworkRequest(networkPayloads, accessToken);
      }
    } catch (e) {
      console.error(e);
      toast({
        title: 'Provider Creation Failed',
        description: 'Unknown Error while creating provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function editHttpProvider() {
    try {
      const accessToken = (await getFirebaseIdToken()) ?? '';

      const providerDataFromDB = await getProviderById(
        accessToken,
        providerDetails.httpProviderId
      );
      const isProviderEdited = compareProviders(providerDetails, providerDataFromDB);

      let payload: ProviderDetails = {
        ...providerDetails,
        isActive: providerDataFromDB?.isActive ?? false,
      };

      if (isProviderEdited) {
        payload = {
          ...payload,
          isActive: false,
        };
      }

      const message = await editProvider({
        providerDetails: payload,
        selectedNetworkRequest,
        accessToken,
      });

      if (!message.isSuccess) {
        toast({
          title: 'Provider Updation Failed...........',
          description: message.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      } else {
        dispatch(updateProviderDetails(payload));
        setButtonLoading(false);

        let networkPayloads: {
          networkRequests: any[];
          httpProviderId: string;
          selectedInstances: any[];
          isCascading: boolean;
        } = {
          networkRequests: [],
          httpProviderId: message.provider.httpProviderId,
          selectedInstances: selectedInstances,
          isCascading: true,
        };

        networkPayloads.networkRequests = selectedNetworkRequest.map((request) => {
          let payload = JSON.parse(JSON.stringify(request));

          delete payload['cookies'];
          delete payload['request']['headers'];

          return {
            ...payload,
          };
        });

        saveProviderNetworkRequest(networkPayloads, accessToken);

        if (isEdit || isProviderEdited || !payload?.isActive) {
          dispatch(updateActiveStep(activeStep + 1));
        } else {
          dispatch(resetParamsAdded());
          navigate('/my-providers');
        }
      }
    } catch (e) {
      toast({
        title: 'Provider Updation Failed',
        description: 'Unknown Error while updating provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  const handleUserAgentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserAgent({ ...userAgent, [e.target.name]: e.target.value });
  };

  const handleParamsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setParamsAdded({ ...paramsAdded, [e.target.name]: e.target.value }));
  };

  return (
    <>
      <Box width="100%">
        <VStack
          spacing={5}
          align="stretch"
          p={7}
          pb={0}
          h="calc(100vh - 240px)"
          overflowY="auto"
          overflowX="hidden"
        >
          <Box width="100%">
            <Flex direction="column" gap="2">
              <Heading className="regular-font">User Experience</Heading>
              <Text className="medium-font" lineHeight="24px" color="#98A2B3">
                The following information will help guide your users correctly
              </Text>
            </Flex>
          </Box>
          <Flex w="full" direction="column" gap={5}>
            <Flex justifyContent="flex-start" gap={12}>
              <FormControl isInvalid={!!nameError} w="400px">
                <Text color="gray.600" ml="5px" className="small-font">
                  Provider Name
                </Text>
                <InputGroup>
                  <Input
                    onChange={(e) => {
                      setNameError('');
                      updateField('name', e.target.value);
                    }}
                    placeholder="Provider Name"
                    required={true}
                    value={providerName}
                    px={5}
                    py={6}
                    borderRadius={15}
                    fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                  />
                  {providerNameLoading && (
                    <InputRightElement width="4.5rem">
                      <Spinner />
                    </InputRightElement>
                  )}
                </InputGroup>
                <FormErrorMessage>{nameError}</FormErrorMessage>
              </FormControl>
              <FormControl w="400px" isInvalid={!!descriptionError}>
                <Text ml="5px" color="gray.600" className="small-font">
                  Provider Description
                </Text>
                <InputGroup>
                  <Input
                    onChange={(e) => {
                      setDescriptionError('');
                      updateField('description', e.target.value);
                    }}
                    placeholder="Provider Description"
                    required={true}
                    value={providerDescription}
                    px={5}
                    py={6}
                    borderRadius={15}
                    fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                  />
                  {providerDetailsLoading && (
                    <InputRightElement width="4.5rem">
                      <Spinner />
                    </InputRightElement>
                  )}
                </InputGroup>
                <FormErrorMessage>{descriptionError}</FormErrorMessage>
              </FormControl>
            </Flex>
            <FormControl isInvalid={!!loginUrlError} w="445px" position="relative">
              <Text ml="5px" color="gray.600" className="small-font">
                Login Url
              </Text>
              <Input
                ref={inputRef}
                value={loginUrl}
                onChange={(e) => {
                  setLoginUrlError('');
                  updateField('loginUrl', e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  // Use setTimeout to allow click events on suggestions to fire before hiding
                  // setTimeout(() => setShowSuggestions(false), 200)
                  setShowSuggestions(false);
                }}
                placeholder="Login Url"
                fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                style={{
                  borderRadius: '15px',
                  border: '1px solid #D0D5DD',
                  padding: '6px 20px',
                  height: '50px',
                }}
              />
              {showSuggestions && filteredAndSortedSuggestions.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left="0"
                  width="100%"
                  maxHeight="200px"
                  overflowY="auto"
                  backgroundColor="white"
                  borderRadius="0 0 15px 15px"
                  boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                  zIndex="1"
                >
                  <VStack align="stretch" spacing={0}>
                    {filteredAndSortedSuggestions.map((url, index) => (
                      <Box
                        key={index}
                        p={2}
                        cursor="pointer"
                        _hover={{ backgroundColor: 'gray.100' }}
                        onClick={() => {
                          updateField('loginUrl', url);
                          setShowSuggestions(false);
                          inputRef.current?.focus();
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {url}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
              <FormErrorMessage>{loginUrlError}</FormErrorMessage>
            </FormControl>
            <Flex justifyContent="flex-start" gap={12}>
              <FormControl w="400px">
                <Text ml="5px" color="gray.600" className="small-font">
                  Expected Page Url
                </Text>
                <Select
                  placeholder="Expected Page Url"
                  value={expectedPageUrl}
                  onChange={(e) => {
                    updateField('expectedPageUrl', e.target.value);
                  }}
                  fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                  style={{
                    color: 'black',
                    borderRadius: '15px',
                    border: '1px solid #D0D5DD',
                    backgroundColor: 'transparent',
                    padding: '6px 5px',
                    paddingLeft: '20px',
                    paddingRight: '35px',
                    height: '50px',
                  }}
                >
                  {[...uniquePagesVisted].map((pageUrl: string, i: number) => (
                    <option key={`expected_page_url_${pageUrl}_${i}`} value={pageUrl}>
                      {pageUrl}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl w="445px">
                <Text ml="5px" color="gray.600" className="small-font">
                  Page Title
                </Text>
                <Flex alignItems="center" gap={5}>
                  <InputGroup>
                    <Input
                      onChange={(e) => {
                        updateField('pageTitle', e.target.value);
                      }}
                      placeholder="Page Title"
                      required={true}
                      value={pageTitle}
                      px={5}
                      py={6}
                      borderRadius={15}
                      fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                    />
                  </InputGroup>
                  {/* <Tooltip
										whiteSpace='pre-line'
										label='something here....'
									>
										<Icon
											as={QuestionOutlineIcon}
											w={6}
											h={6}
											color='gray.600'
											cursor='pointer'
										/>
									</Tooltip> */}
                </Flex>
              </FormControl>
            </Flex>
            <Flex direction="column">
              <Text ml="5px" color="gray.600" className="small-font">
                Steps user needs to follow to open the information page
              </Text>
              <Textarea
                w="400px"
                px={5}
                py={6}
                borderRadius={15}
                placeholder="One step per line"
                value={stepsToFollow}
                onChange={(e) => updateField('stepsToFollow', e.target.value)}
                fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
              />
            </Flex>
            <Flex>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" color="gray.600" className="small-font">
                  Advance Options
                </FormLabel>
                <Switch
                  id="email-alerts"
                  isChecked={showAdvanceFields}
                  colorScheme="blackAlpha"
                  onChange={() => setShowAdvanceFields(!showAdvanceFields)}
                />
              </FormControl>
            </Flex>
            {showAdvanceFields && (
              <>
                <Flex justifyContent="flex-start" gap={10}>
                  <Flex direction="column" gap={2}>
                    <Flex alignItems="center" gap={4}>
                      <Text ml="5px" color="gray.600" className="small-font">
                        Geolocation
                      </Text>
                    </Flex>

                    <FormControl id="geolocation" w="400px">
                      <Select
                        //do not delete
                        // value={isDynamicGeo ? '' : geoLocation}
                        value={geoLocation}
                        onChange={(e) => {
                          updateField('geoLocation', e.target.value);
                        }}
                        placeholder="Select country"
                        //do not delete
                        // isDisabled={isDynamicGeo}
                        fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                        style={{
                          color: 'black',
                          borderRadius: '15px',
                          border: '1px solid #D0D5DD',
                          backgroundColor: 'transparent',
                          padding: '6px 5px',
                          paddingLeft: '20px',
                          paddingRight: '35px',
                          height: '50px',
                        }}
                      >
                        {countryCodes.map((countryCode: { Code: string; Name: string }) => (
                          <option key={countryCode.Code} value={countryCode.Code}>
                            {countryCode.Name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    {/* do not delete */}
                    {/* <Flex alignItems="center" gap={2}>
                      <Checkbox
                        isChecked={isDynamicGeo}
                        onChange={(e) => {
                          const isDynamic = e.target.checked;
                          setIsDynamicGeo(isDynamic);
                          if (isDynamic) {
                            updateField('geoLocation', '{{DYNAMIC_GEO}}');
                          } else {
                            // Reset to empty string or first country code if available
                            updateField('geoLocation', countryCodes.length > 0 ? countryCodes[0].Code : '');
                          }
                        }}
                        colorScheme="blackAlpha"
                      >
                        <Text color="gray.600" className="small-font">
                          Dynamic Geolocation
                        </Text>
                      </Checkbox>
                      <Tooltip
                        label="When enabled, geolocation will be determined dynamically at runtime instead of using a fixed location"
                        aria-label="Dynamic geolocation tooltip"
                      >
                        <Icon
                          as={QuestionOutlineIcon}
                          w={4}
                          h={4}
                          color="gray.600"
                          cursor="pointer"
                        />
                      </Tooltip>
                    </Flex> */}
                  </Flex>
                  <Flex direction="column">
                    <Text ml="5px" color="gray.600" className="small-font">
                      Provider Type
                    </Text>
                    <FormControl id="provider-type" w="400px">
                      <Tooltip
                        label={
                          providerType === 'PUBLIC'
                            ? 'Public Providers Need Successful Proof Generation to be published'
                            : ''
                        }
                        aria-label="A tooltip"
                      >
                        <Select
                          value={providerType}
                          onChange={(e) => {
                            updateField('providerType', e.target.value);
                          }}
                          fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                          style={{
                            color: 'black',
                            borderRadius: '15px',
                            border: '1px solid #D0D5DD',
                            backgroundColor: 'transparent',
                            padding: '6px 5px',
                            paddingLeft: '20px',
                            paddingRight: '35px',
                            height: '50px',
                          }}
                        >
                          <option value="PRIVATE">Private</option>

                          <option value="PUBLIC">Public</option>
                        </Select>
                      </Tooltip>
                    </FormControl>
                  </Flex>
                </Flex>
                <Flex>
                  <Box w="full">
                    <Flex gap="50px">
                      <Box flex={1}>
                        <Flex gap={5}>
                          <Flex direction="column" gap={2} py={5} px={1} flex={1}>
                            <Heading
                              ml="5px"
                              color="gray.500"
                              className="small-font"
                              fontWeight={600}
                            >
                              Variable Details
                            </Heading>

                            <Flex direction="column" gap={5}>
                              {selectedInstances.map((instance: any, i: any) => (
                                <Card
                                  variant="outline"
                                  key={i}
                                  position="relative"
                                  borderRadius={20}
                                >
                                  {selectedInstances.length > 1 && (
                                    <IconButton
                                      aria-label="Search database"
                                      icon={<DeleteIcon fontSize="1rem" />}
                                      position="absolute"
                                      right={4}
                                      top={3}
                                      variant="outline"
                                      colorScheme="black"
                                      size="sm"
                                      onClick={() => {
                                        dispatch(deleteSelectedInstance(i));
                                      }}
                                    />
                                  )}
                                  <CardBody>
                                    <Flex direction="column" gap={2}>
                                      <Flex
                                        alignItems="center"
                                      // mt={7}
                                      >
                                        <Flex alignItems="center" w="95%" gap={5}>
                                          <Text
                                            style={{ textWrap: 'nowrap' }}
                                            color="gray.600"
                                            whiteSpace="nowrap"
                                            textOverflow="ellipsis"
                                            minW="150px"
                                            className="small-font"
                                          >
                                            Request URL:
                                          </Text>
                                          <Text
                                            style={{ textWrap: 'nowrap' }}
                                            color="gray.600"
                                            whiteSpace="nowrap"
                                            textOverflow="ellipsis"
                                            minW="150px"
                                            className="small-font"
                                          >
                                            {
                                              Object.values(getAllRequest)[
                                                instance?.responseSelection?.requestIndex
                                              ]?.url
                                            }
                                          </Text>
                                        </Flex>
                                      </Flex>
                                      <Flex direction="column" gap={2}>
                                        <Flex alignItems="center" w="95%">
                                          <Text
                                            style={{ textWrap: 'nowrap' }}
                                            color="gray.600"
                                            whiteSpace="nowrap"
                                            textOverflow="ellipsis"
                                            minW="150px"
                                            className="small-font"
                                          >
                                            Variable Name:
                                          </Text>
                                          <Text
                                            border="1px solid #D0D5DD"
                                            px={5}
                                            py={3}
                                            borderRadius="15px"
                                            color="#000"
                                            whiteSpace="nowrap"
                                            textOverflow="ellipsis"
                                            overflow="hidden"
                                            w="full"
                                            fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                                          // cursor='not-allowed'
                                          >
                                            {instance?.variableName ?? ''}
                                          </Text>
                                        </Flex>
                                      </Flex>
                                      <Flex alignItems="center" w="95%">
                                        <Text
                                          style={{ textWrap: 'nowrap' }}
                                          color="gray.600"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          minW="150px"
                                          className="small-font"
                                        >
                                          Description:
                                        </Text>

                                        <Input
                                          value={instance.responseSelection.description}
                                          style={{
                                            color: 'black',
                                            borderRadius: '15px',
                                            border: '1px solid #D0D5DD',
                                          }}
                                          px={5}
                                          py={6}
                                          borderRadius={15}
                                          fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                                          onChange={(e) => {
                                            dispatch(
                                              updateResponseSelection({
                                                index: i,
                                                responseSelection: {
                                                  xPath: instance.responseSelection.xPath,
                                                  jsonPath:
                                                    instance.responseSelection.jsonPath,
                                                  responseMatch:
                                                    instance.responseSelection
                                                      .responseMatch,
                                                  description: e.target.value,
                                                  invariant: instance.invariant,
                                                  matchType:
                                                    instance.responseSelection.matchType,
                                                  requestIndex:
                                                    instance.responseSelection
                                                      .requestIndex,
                                                },
                                              })
                                            );
                                          }}
                                        />
                                      </Flex>
                                      <Flex alignItems="center" w="95%">
                                        <Text
                                          style={{ textWrap: 'nowrap' }}
                                          color="gray.600"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          minW="150px"
                                          className="small-font"
                                        >
                                          Regex:
                                        </Text>

                                        <Input
                                          value={instance.responseSelection.responseMatch}
                                          style={{
                                            color: 'black',
                                            borderRadius: '15px',
                                            border: '1px solid #D0D5DD',
                                          }}
                                          px={5}
                                          py={6}
                                          borderRadius={15}
                                          fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                                          onChange={(e) => {
                                            dispatch(
                                              updateResponseSelection({
                                                index: i,
                                                responseSelection: {
                                                  xPath: instance.responseSelection.xPath,
                                                  jsonPath:
                                                    instance.responseSelection.jsonPath,
                                                  responseMatch: e.target.value,
                                                  invariant: instance.invariant,
                                                  matchType:
                                                    instance.responseSelection.matchType,
                                                  requestIndex:
                                                    instance.responseSelection
                                                      .requestIndex,
                                                },
                                              })
                                            );
                                          }}
                                        />
                                      </Flex>

                                      <Flex alignItems="center" w="95%">
                                        <Text
                                          style={{ textWrap: 'nowrap' }}
                                          color="gray.600"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          minW="150px"
                                          className="small-font"
                                        >
                                          JsonPath:
                                        </Text>

                                        <Input
                                          value={instance.responseSelection.jsonPath}
                                          style={{
                                            color: 'black',
                                            borderRadius: '15px',
                                            border: '1px solid #D0D5DD',
                                          }}
                                          px={5}
                                          py={6}
                                          borderRadius={15}
                                          fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                                          onChange={(e) => {
                                            dispatch(
                                              updateResponseSelection({
                                                index: i,
                                                responseSelection: {
                                                  xPath: instance.responseSelection.xPath,
                                                  jsonPath: e.target.value,
                                                  responseMatch:
                                                    instance.responseSelection
                                                      .responseMatch,
                                                  invariant: instance.invariant,
                                                  matchType:
                                                    instance.responseSelection.matchType,
                                                  requestIndex:
                                                    instance.responseSelection
                                                      .requestIndex,
                                                },
                                              })
                                            );
                                          }}
                                        />
                                      </Flex>
                                      <Flex alignItems="center" w="95%">
                                        <Text
                                          style={{ textWrap: 'nowrap' }}
                                          color="gray.600"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          minW="150px"
                                          className="small-font"
                                        >
                                          Xpath:
                                        </Text>

                                        <Input
                                          value={instance.responseSelection.xPath}
                                          style={{
                                            color: 'black',
                                            borderRadius: '15px',
                                            border: '1px solid #D0D5DD',
                                          }}
                                          px={5}
                                          py={6}
                                          borderRadius={15}
                                          fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                                          onChange={(e) => {
                                            dispatch(
                                              updateResponseSelection({
                                                index: i,
                                                responseSelection: {
                                                  xPath: e.target.value,
                                                  jsonPath:
                                                    instance.responseSelection.jsonPath,
                                                  responseMatch:
                                                    instance.responseSelection
                                                      .responseMatch,
                                                  invariant: instance.invariant,
                                                  matchType:
                                                    instance.responseSelection.matchType,
                                                  requestIndex:
                                                    instance.responseSelection
                                                      .requestIndex,
                                                },
                                              })
                                            );
                                          }}
                                        />
                                      </Flex>
                                      <Flex alignItems="center" w="95%">
                                        <Text
                                          style={{ textWrap: 'nowrap' }}
                                          color="gray.600"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          minW="150px"
                                          className="small-font"
                                        >
                                          Match Type
                                        </Text>
                                        <FormControl id="geolocation">
                                          <Select
                                            value={instance.responseSelection.matchType}
                                            onChange={(e) => {
                                              dispatch(
                                                updateResponseSelection({
                                                  index: i,
                                                  responseSelection: {
                                                    xPath:
                                                      instance.responseSelection.xPath,
                                                    jsonPath:
                                                      instance.responseSelection.jsonPath,
                                                    responseMatch:
                                                      instance.responseSelection
                                                        .responseMatch,
                                                    invariant: instance.invariant,
                                                    matchType: e.target.value,
                                                    requestIndex:
                                                      instance.responseSelection
                                                        .requestIndex,
                                                  },
                                                })
                                              );
                                            }}
                                            fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                                            style={{
                                              color: 'black',
                                              borderRadius: '15px',
                                              border: '1px solid #D0D5DD',
                                              backgroundColor: 'transparent',
                                              padding: '6px 5px',
                                              paddingLeft: '20px',
                                              paddingRight: '35px',
                                              height: '50px',
                                            }}
                                          >
                                            <option value="greedy">Greedy</option>
                                            <option value="lazy">Lazy</option>
                                          </Select>
                                        </FormControl>
                                      </Flex>
                                    </Flex>
                                  </CardBody>
                                </Card>
                              ))}
                            </Flex>
                          </Flex>
                        </Flex>
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
                <Flex direction="column">
                  <Text color="gray.600" className="small-font" ml="5px" mb={2}>
                    Setting Parameters
                  </Text>
                  <Flex alignItems="center" gap={5} w="100%">
                    <Card
                      variant="outline"
                      position="relative"
                      borderRadius={20}
                      w="100%"
                    >
                      <CardBody>
                        <Flex direction="column" gap={2}>
                          <Alert
                            status="warning"
                            mb={2}
                            w="95%"
                            borderRadius={15}
                            size="sm"
                          >
                            <AlertIcon />
                            <Text>
                              This is only temporary for testing purposes, once you leave
                              the page, the changes will be lost. For production, you will
                              have to set the parameters manually in the code. For more
                              information, please refer to the{' '}
                              <a
                                href="https://docs.reclaimprotocol.org/js/advanced-configuration#setting-parameters"
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  textDecoration: 'underline',
                                  marginLeft: '5px',
                                }}
                              >
                                documentation
                              </a>
                              .
                            </Text>
                          </Alert>
                          {selectedInstances.map((instance: any, i: any) => (
                            <Flex alignItems="center" w="95%" key={i}>
                              <Text
                                color="gray.600"
                                p={4}
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                                className="small-font"
                              >
                                {instance?.variableName ?? ''}:
                              </Text>
                              <Input
                                value={paramsAdded[instance?.variableName ?? '']}
                                style={{
                                  color: 'black',
                                  borderRadius: '15px',
                                  border: '1px solid #D0D5DD',
                                }}
                                px={5}
                                py={6}
                                borderRadius={15}
                                fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                                name={instance?.variableName ?? ''}
                                onChange={handleParamsChange}
                              />
                            </Flex>
                          ))}
                        </Flex>
                      </CardBody>
                    </Card>
                  </Flex>
                </Flex>
                <Flex direction="column">
                  <Text color="gray.600" className="small-font" ml="5px">
                    JS Injection
                  </Text>
                  <Flex alignItems="center" gap={5}>
                    <Textarea
                      w="400px"
                      px={5}
                      py={6}
                      borderRadius={15}
                      placeholder="JS injection to run on website"
                      value={customInjection}
                      fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                      onChange={(e: { target: { value: SetStateAction<string> } }) =>
                        setCustomInjection(e.target.value)
                      }
                    />
                    <Tooltip
                      whiteSpace="pre-line"
                      label="Providers with Custom Injection are subjected to admin approval"
                    >
                      <Icon
                        as={QuestionOutlineIcon}
                        w={6}
                        h={6}
                        color="gray.600"
                        cursor="pointer"
                      />
                    </Tooltip>
                  </Flex>
                </Flex>
                <Flex justifyContent="flex-start" gap={12}>
                  <FormControl w="400px">
                    <Text color="gray.600" ml="5px" className="small-font">
                      Android User Agent
                    </Text>
                    <InputGroup>
                      <Input
                        onChange={handleUserAgentChange}
                        placeholder="Provider Name"
                        name="android"
                        required={true}
                        value={userAgent?.android ?? ''}
                        px={5}
                        py={6}
                        borderRadius={15}
                        fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                      />
                    </InputGroup>
                  </FormControl>
                  <FormControl w="400px">
                    <Text ml="5px" color="gray.600" className="small-font">
                      IOS User Agent
                    </Text>
                    <InputGroup>
                      <Input
                        onChange={handleUserAgentChange}
                        placeholder="Provider Description"
                        name="ios"
                        required={true}
                        value={userAgent?.ios ?? ''}
                        px={5}
                        py={6}
                        borderRadius={15}
                        fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                      />
                    </InputGroup>
                  </FormControl>
                </Flex>
              </>
            )}
          </Flex>
        </VStack>
        <Box w="full" mt={10} className="bg-secondary" borderRadius={'10'} px={30} py={4}>
          <Flex w="full" justifyContent="space-between">
            <Button
              backgroundColor="transparent"
              border="1px solid gray"
              borderRadius={10}
              fontWeight="normal"
              _hover={{
                fontWeight: 'bold',
              }}
              minWidth={'100px'}
              // isDisabled={buttonLoading}
              onClick={handleGoBack}
            >
              Back
            </Button>
            <Flex alignItems="center" gap={3}>
              <Text color="gray.500" className="medium-font">
                5/7
              </Text>
              <Button
                backgroundColor="black"
                color="white"
                border="1px solid black"
                borderRadius={10}
                fontWeight="normal"
                _hover={{ backgroundColor: 'gray.800' }}
                // isLoading={buttonLoading}
                onClick={handleNext}
                minWidth={'100px'}
              >
                {providerDetails?.httpProviderId?.length ? 'Edit Provider' : 'Submit'}
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </>
  );
};
